/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, OutlinedInput, Chip, Grid, styled, ListItemText } from '@mui/material'
import { MenuItem, Typography, SelectField } from '@carto/meridian-ds/components'
import { useAppSelector } from 'app/state'
import { getEnrichedDatasetById } from 'features/builder/state/keplerGlSlice'
import { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import TypeTag from 'features/common/ui/TypeTag'

const ButtonWrapper = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer'
}))

const ColumnsGrid = styled(Grid)(({ theme }) => ({
  gap: theme.spacing(1),
  margin: theme.spacing(0.5, 0),
  height: theme.spacing(3),
  overflow: 'auto',

  '& .MuiGrid-root': {
    display: 'flex'
  }
}))

function MultipleColumnsDropdown({
  source,
  columns,
  labelId,
  selectAll,
  onChange
}: {
  source: string
  columns: Record<string, string>[]
  labelId: string
  selectAll?: boolean
  onChange: (columns: Record<string, string>[]) => void
}) {
  const sourceColumns = useAppSelector((state) => getEnrichedDatasetById(state, source as string))?.metadata.schema
  const secondSourceFromDataTree = useAppSelector((state) => state.analysis.secondSourceTable)?.schema
  const options = (sourceColumns || secondSourceFromDataTree)?.map((field) => ({
    value: field.name,
    label: field.name,
    type: field.type
  }))
  const showClearButton = columns.length >= 1
  const [openMenuList, setOpenMenuList] = useState(false)
  const [initialValues, setInitialValues] = useState(false)

  useEffect(() => {
    if (selectAll && columns.length < options?.length && !initialValues) {
      onChange(options)
      setInitialValues(true)
    }
  }, [onChange, initialValues, selectAll, options, columns])

  const clearSelected = () => {
    onChange([])
  }

  return (
    <Box mb={2}>
      <SelectField
        displayEmpty
        label={<FormattedMessage id={labelId} />}
        labelSecondary={
          showClearButton && (
            <ButtonWrapper onClick={clearSelected}>
              <Typography variant="caption" weight="medium" color="primary" noWrap>
                <FormattedMessage id="analysis.multipleDropdown.clear" />
              </Typography>
            </ButtonWrapper>
          )
        }
        labelId={`${source}-label`}
        id={`${source}-label`}
        multiple
        value={columns?.map((column) => column.label)}
        onClick={(e: any) => {
          const isSelectingItems = openMenuList && typeof e.target.value === 'object'
          if (!options?.length || isSelectingItems) return
          setOpenMenuList(!openMenuList)
        }}
        open={openMenuList}
        disabled={!options?.length}
        onChange={(e) => {
          const values = e.target.value as string[]
          const map = options?.filter((option) => values.includes(option.value)) as Record<string, string>[]
          values && onChange(map)
        }}
        menuProps={{
          PaperProps: {
            style: {
              maxHeight: 320,
              width: 300
            }
          }
        }}
        input={<OutlinedInput label="Chip" />}
        renderValue={(columns: any) => (
          <ColumnsGrid container>
            {columns?.map((column, index) => {
              return (
                <Grid item key={`${column}-${index}-1`}>
                  <Chip
                    color="default"
                    size="small"
                    label={column}
                    onDelete={() => {
                      const newValues = columns.filter((c) => c !== column)
                      const updatedValues = options?.filter((option) => newValues.includes(option.value)) as Record<
                        string,
                        string
                      >[]
                      updatedValues && onChange(updatedValues)
                    }}
                  />
                </Grid>
              )
            })}
            {!columns?.length && (
              <Grid item mt={0.25}>
                <Typography variant="body2" color="text.hint">
                  <FormattedMessage id="analysis.addColumn.selectColumns" />
                </Typography>
              </Grid>
            )}
          </ColumnsGrid>
        )}
        size="small"
      >
        {options?.map((column, index) => (
          <MenuItem key={`${column.name}-${index}-2`} value={column.value}>
            <ListItemText>{column.label}</ListItemText>
            <TypeTag type={column.type} />
          </MenuItem>
        ))}
      </SelectField>
    </Box>
  )
}

export default MultipleColumnsDropdown
